import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Form, Spin, message } from 'antd';
import moment from 'moment';
import intl from 'react-intl-universal';

import CustomForm, { IFieldItem, IMyFormRef } from '@/components/CustomForm';
import TableForm from '@/components/Reservation/TableForm';
import { fetchSysBusinessDateByTime } from '@/api/order';
import { DATE_TIME_FORMAT, OccType, SubOrderStatus } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';
import {
  childAccompanyList,
  childAccompanyMap,
  contactCustomerList,
  contactCustomerMap,
  foreignerAccompanyList,
  foreignerAccompanyMap,
  olderAccompanyList,
  olderAccompanyMap,
  parkingDemandList,
  parkingDemandMap,
  roomArrangeList,
  roomArrangeMap,
  smokeList,
  smokeListMap,
  takeCareList,
} from '../../helper';
import styles from './index.module.scss';

const keyFnMap = {
  acStatus: (val) => {
    if (val === 1) {
      return intl.get('roomstate_take_care_edit_form_task_status_done');
    }
    if (val === 0) {
      return intl.get('roomstate_take_care_edit_form_task_need_concat');
    }
    return '';
  },
  contactCustomer: (val) => contactCustomerMap[val],
  parkingDemand: (val) => parkingDemandMap[val],
  smokeFreeHandle: (val) => smokeListMap[val],
  elderAccompany: (val) => olderAccompanyMap[val],
  minorAccompany: (val) => childAccompanyMap[val],
  foreignerAccompany: (val) => foreignerAccompanyMap[val],
  roomArrangeSpecialDemand: (val) => roomArrangeMap[val],
};

export type TakeCareTabItemRef = { getSubmitValues: () => Promise<any> };

const TakeCareTabItem = React.forwardRef<TakeCareTabItemRef, { detail: any; loading: boolean }>(
  ({ detail, loading }, ref) => {
    // 指定预抵时间的营业日
    const [businessDate, setBusinessDate] = useState<any>();

    const formRef = useRef<IMyFormRef>();

    const formatDetail = useMemo(() => {
      return {
        ...detail,
        expectedArrivalTime: detail.expectedArrivalTime
          ? moment(detail.expectedArrivalTime)
          : undefined,
      };
    }, [detail]);

    const formAttr: Array<IFieldItem> = useMemo(() => {
      return [
        {
          label: intl.get('roomstate_take_care_edit_form_field_task_status'),
          key: 'acStatus',
          type: 'radio',
          dataSource: takeCareList?.filter((item) => item.value !== 2),
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_task_status_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
          fieldProps: {
            extra: intl.get('roomstate_take_care_edit_form_field_task_status_hint'),
          },
        },
        {
          label: intl.get('roomstate_take_care_edit_form_field_concat_status'),
          key: 'contactCustomer',
          type: 'radio',
          dataSource: contactCustomerList,
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_concat_status_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
        },
        {
          label: intl.get('roomstate_take_care_edit_form_field_parking'),
          key: 'parkingDemand',
          type: 'radio',
          dataSource: parkingDemandList,
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_parking_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
        },
        {
          label: intl.get('roomstate_take_care_edit_form_field_smokeless'),
          key: 'smokeFreeHandle',
          type: 'radio',
          dataSource: smokeList,
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_smokeless_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
        },
        {
          label: intl.get('roomstate_take_care_edit_form_field_elder'),
          key: 'elderAccompany',
          type: 'radio',
          dataSource: olderAccompanyList,
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_elder_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
        },
        {
          label: intl.get('roomstate_take_care_edit_form_field_child'),
          key: 'minorAccompany',
          type: 'radio',
          dataSource: childAccompanyList,
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_child_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
        },
        {
          label: intl.get('roomstate_take_care_edit_form_field_foreigner'),
          key: 'foreignerAccompany',
          type: 'radio',
          dataSource: foreignerAccompanyList,
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_foreigner_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
        },
        {
          label: intl.get('roomstate_take_care_edit_form_field_room_special'),
          key: 'roomArrangeSpecialDemand',
          type: 'radio',
          dataSource: roomArrangeList,
          rules: [
            {
              required: true,
              message: intl.get('roomstate_take_care_edit_form_field_room_special_message'),
            },
          ],
          componentProps: { disabled: detail.roomStatus !== SubOrderStatus.Reservation },
        },
        {
          key: 'content',
          shouldUpdate: (pre, cur) => Object.keys(keyFnMap).some((key) => pre[key] !== cur[key]),
          component: ({ getFieldValue }) => {
            const valueList = Object.keys(keyFnMap)
              .map((key) => keyFnMap[key](getFieldValue(key)))
              ?.filter((val) => val);
            return (
              <Form.Item
                label={intl.get('roomstate_take_care_edit_form_field_task_detail')}
                className={styles.contentText}
              >
                {valueList?.join('，')}
              </Form.Item>
            );
          },
        },
        {
          label: intl.get('app_reservation_eta_time'),
          key: 'expectedArrivalTime',
          type: 'datePicker',
          componentProps: {
            disabled:
              detail.roomStatus !== SubOrderStatus.Reservation ||
              detail?.occType === OccType.TimeRENT,
            disabledDate: (currentDate) =>
              currentDate < moment(businessDate).hour(0).minute(0).second(0).millisecond(0) ||
              currentDate >
                moment(businessDate)
                  .clone()
                  .add(1, 'day')
                  .hour(23)
                  .minute(59)
                  .second(59)
                  .millisecond(0),
          },
        },
        {
          label: intl.get('app_reservation_checkin_remark'),
          key: 'checkinRemark',
          type: 'textArea',
          placeholder: intl.get('roomstate_take_care_edit_form_field_checkin_remark_placeholder'),
          rules: [
            {
              message: intl.get('roomstate_take_care_edit_form_field_checkin_remark_message'),
              max: 200,
            },
          ],
          componentProps: {
            autoSize: true,
            showCount: true,
            maxLength: 200,
            disabled: detail.roomStatus !== SubOrderStatus.Reservation,
          },
        },
      ];
    }, [detail.roomStatus, detail?.occType, businessDate]);

    const getBusinessDateByArriveTime = async (time) => {
      try {
        const businessRes = await fetchSysBusinessDateByTime({
          time: getTimeByUtcOffset(time, DATE_TIME_FORMAT),
        });
        const localDate = businessRes?.data?.localDate;
        setBusinessDate(localDate);
      } catch (error) {
        //
      }
    };

    useEffect(() => {
      if (detail?.expectedArrivalTime) {
        getBusinessDateByArriveTime(detail?.expectedArrivalTime);
      }
    }, [detail]);

    useImperativeHandle(
      ref,
      () => ({
        getSubmitValues() {
          return new Promise((resolve, reject) => {
            formRef.current
              .validateFields()
              .then((values) => {
                const result = { ...values };
                const start = moment(businessDate).hour(6).minute(0).second(0).millisecond(0);
                const end = moment(businessDate)
                  .clone()
                  .add(1, 'day')
                  .hour(5)
                  .minute(59)
                  .second(59)
                  .millisecond(0);
                if (
                  moment(result?.expectedArrivalTime) < start ||
                  moment(result?.expectedArrivalTime) > end
                ) {
                  message.error(
                    intl.get('roomstate_take_care_edit_form_cross_date', {
                      startDate: getTimeByUtcOffset(start, DATE_TIME_FORMAT),
                      endDate: getTimeByUtcOffset(end, DATE_TIME_FORMAT),
                    }),
                  );
                  reject();
                }
                if (result?.expectedArrivalTime) {
                  result.expectedArrivalTime = moment(result.expectedArrivalTime).valueOf();
                }
                resolve(result);
              })
              .catch(reject);
          });
        },
      }),
      [businessDate],
    );

    return (
      <>
        <Spin spinning={loading}>
          <div className={styles.container}>
            <TableForm>
              <CustomForm
                ref={formRef}
                labelCol={{ span: 5 }}
                labelAlign="left"
                formAttr={formAttr}
                value={formatDetail}
              />
            </TableForm>
          </div>
        </Spin>
      </>
    );
  },
);

export default TakeCareTabItem;
